import { useState, useEffect } from 'react';
import { Dialog, Box, Typography, IconButton, Button, Stack, TextField, MenuItem, ReportOutlinedIcon, 
    CloseIcon 
} from 'src/components';
import { useIsMobile, useSnackbar } from 'src/hooks';
import { useAuth } from 'src/utils';
import { createComment } from 'src/utils';
import { DesktopProfileModal } from 'src/pages/profile'
import { useNavigate } from 'react-router-dom';

const REPORT_REASONS = [
  { value: 'offensive', label: 'Offensive content' },
  { value: 'inaccurate', label: 'Inaccurate information' },
  { value: 'spam', label: 'Spam / Promotion' },
  { value: 'other', label: 'Other' },
];

export default function ReportIssue({ articleId }) {
    const { user } = useAuth();
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const { openSnackbar } = useSnackbar();

    const [displayLogin, setDisplayLogin] = useState(false);
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState(user?.email || '');
    const [reason, setReason] = useState('');
    const [details, setDetails] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setEmail(user?.email);
    }, [user?.email]);

    const handleOpen = () => {
        if (user === null) {
            isMobile ? navigate('/profile') : setDisplayLogin(true);
            return;
        }
        setOpen(true);
    }
    const handleClose = () => setOpen(false);

    const handleSubmit = async () => {
        // Require all fields
        if ((!user && !email) || !reason || !details) return;

        setLoading(true);
        try {
        await createComment({
            articleId,
            reason,
            text: details,
            type: "Report"
        });
        handleClose();
        openSnackbar("Issue reported successfully", "success");
        } catch (err) {
        console.error(err);
        openSnackbar("Report issue failed", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        {/* Trigger Button */}
        <Button
            variant="text"
            onClick={handleOpen}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 'bold',
                color: 'text.primary',
                px: 1.5,
                opacity: 0.8
            }}
            startIcon={<ReportOutlinedIcon />}
        >
            <Typography variant="subtitle1" sx={{ml: '-10px'}}>Found an issue?</Typography>
            <Typography variant="subtitle1" sx={{ color: 'warning.light' }}>Report it</Typography>
        </Button>

        {/* Dialog */}
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="xs"
            fullWidth
            PaperProps={{
            sx: {
                borderRadius: isMobile ? 0 : 2,
                overflow: 'hidden',
            },
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', overflowY: 'auto' }}>
            
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3, bgcolor: '#fff', borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReportOutlinedIcon />
                <Typography variant="h5" fontWeight="bold">Report Issue</Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                <CloseIcon />
                </IconButton>
            </Box>

            {/* Form */}
            <Box sx={{ flex: 1, p: 4, bgcolor: '#fff', borderTopRightRadius: 30, borderTopLeftRadius: 30 }}>
                <Stack spacing={2}>
                {/* Email Field */}
                {!user && (
                    <TextField
                    label="Your Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    fullWidth
                    />
                )}
                {user && (
                    <TextField
                    label="Your Email"
                    type="email"
                    value={email}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    required
                    />
                )}

                {/* Reason Dropdown */}
                <TextField
                    select
                    label="Reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    fullWidth
                    required
                >
                    {REPORT_REASONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                    ))}
                </TextField>

                {/* Additional Details */}
                <TextField
                    label="Additional details"
                    multiline
                    minRows={3}
                    maxRows={6}
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    fullWidth
                    required
                />

                {/* Action Buttons */}
                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={loading || (!user && !email) || !reason || !details}>
                    {loading ? 'Submitting...' : 'Submit'}
                    </Button>
                </Stack>
                </Stack>
            </Box>
            </Box>
        </Dialog>

        <DesktopProfileModal displayLogin={displayLogin} setDisplayLogin={setDisplayLogin}/>
        </>
    );
}
